export const AVAILABLE_ROLES = ["admin", "member"] as const;

/**
 * Roles that exist only so the account can reach Blocks platform endpoints —
 * they are NOT application roles and must never appear in the UI or take part in
 * permission checks. `clouduser` is the platform's own "System User" role (it
 * holds all 146 built-in Blocks permissions); a user carrying it is not thereby
 * an app admin.
 *
 * ⚠ Filter these for DISPLAY and AUTHORIZATION only — never drop them from a
 * write. `POST /iam/v4/iam/me` is a full replace, so echoing back a filtered
 * role list would strip the user's platform access. Use `mergePlatformRoles`
 * on any path that writes roles.
 */
export const PLATFORM_ONLY_ROLES = ["clouduser"] as const;

const normalizeRoleKey = (role?: string | null) =>
  (role ?? "").toLowerCase().replace(/[\s_-]/g, "");

export const isPlatformOnlyRole = (role?: string | null): boolean =>
  PLATFORM_ONLY_ROLES.some((r) => normalizeRoleKey(r) === normalizeRoleKey(role));

/** App-facing roles only — for menus, badges, role pickers, permission checks. */
export const appRoles = (
  roles?: readonly (string | null | undefined)[] | null,
): string[] =>
  (roles ?? []).filter(
    (role): role is string =>
      typeof role === "string" && role.length > 0 && !isPlatformOnlyRole(role),
  );

/**
 * The roles to persist: what the user chose, plus any platform-only roles the
 * record already had. Keeps `clouduser` alive through a full-replace write even
 * though the UI never showed it.
 */
export const mergePlatformRoles = (
  chosen: readonly string[],
  current?: readonly string[] | null,
): string[] => {
  const seen = new Set(chosen.map(normalizeRoleKey));
  return [
    ...chosen,
    ...(current ?? []).filter(
      (role) =>
        isPlatformOnlyRole(role) && !seen.has(normalizeRoleKey(role)),
    ),
  ];
};
/**
 * The roles to persist for a NEW account: what was chosen, plus every
 * platform-only role.
 *
 * An account needs `clouduser` to reach Blocks platform endpoints at all, and
 * nothing grants it after the fact — `mergePlatformRoles` only carries forward
 * what a record ALREADY has, so an account created without it never acquires
 * one. Invited users therefore have to be created with it.
 *
 * This grants platform access, not app authority: `appRoles` strips these from
 * every permission check and every display, so holding `clouduser` does not make
 * anyone an app admin. See `PLATFORM_ONLY_ROLES`.
 */
export const withPlatformRoles = (chosen: readonly string[]): string[] => {
  const seen = new Set(chosen.map(normalizeRoleKey));
  return [
    ...chosen,
    ...PLATFORM_ONLY_ROLES.filter((role) => !seen.has(normalizeRoleKey(role))),
  ];
};

/**
 * The backend's bookkeeping tenant. A user can carry a membership in it while
 * also belonging to a real organization, so it must be filtered out of anything
 * that answers "which orgs does this user actually belong to" — a session must
 * never operate under it.
 */
export const DEFAULT_ORG_ID = "default" as const;

export const isRealOrgId = (organizationId?: string | null): boolean =>
  !!organizationId && organizationId !== DEFAULT_ORG_ID;

export const DISABLED_PERMISSION_VALUE = "permission-disabled" as const;
export const ADMIN_ROLE = "admin" as const;
export const MEMBER_ROLE = "member" as const;

export const UDS_ADMIN_RULE_INDEX = 1 as const;
export const UDS_MEMBER_RULE_INDEX = 2 as const;

export const UDS_SCHEMAS = ["post", "news", "event", "wiki", "group"] as const;
export type UdsSchema = (typeof UDS_SCHEMAS)[number];
